import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { FocusedLayout } from '../../components/layout/FocusedLayout';
import { userAPI } from '../../services/api';
import { useAuth, formatApiErrorDetail } from '../../contexts/AuthContext';
import { useWebSocket } from '../../hooks/useWebSocket';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { formatDate } from '../../utils/helpers';
import { 
  MessageSquare, Send, Edit2, Trash2, Ban, ArrowLeft, Users, Plus, Smile, UserPlus, 
  UserMinus, LogOut, Pin, Search, DollarSign, CreditCard, QrCode, History, Gift, 
  AlertCircle, Check, X, Clock, Zap, CheckCircle, ShieldCheck, Store
} from 'lucide-react';

const REACTION_OPTIONS = ['👍', '❤️', '😂', '😮', '😢', '🎉', '🔥', '💯'];
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MatriseConnectPage = ({ embeddedInConnect = false }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [pinnedIds, setPinnedIds] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openReaction, setOpenReaction] = useState(null);
  const [showNewGroup, setShowNewGroup] = useState(false);
  const [showManage, setShowManage] = useState(false);
  const [showWalletActions, setShowWalletActions] = useState(true);
  const [showPaymentRequest, setShowPaymentRequest] = useState(false);
  const [showSendMoney, setShowSendMoney] = useState(false);
  const [showVoucherShare, setShowVoucherShare] = useState(false);
  const [showReportUser, setShowReportUser] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showUserSearch, setShowUserSearch] = useState(false);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [searchingUsers, setSearchingUsers] = useState(false);
  const [typingUsers, setTypingUsers] = useState({});
  const endRef = useRef(null);
  const selectedRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  useEffect(() => { selectedRef.current = selected; }, [selected]);

  const handleWsMessage = useCallback((data) => {
    if (!data || !data.type) return;
    const cur = selectedRef.current;

    if (data.type === 'message_new') {
      if (cur && cur.id === data.conversation_id) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === data.message.id)) return prev;
          return [...prev, data.message];
        });
        userAPI.markConversationRead(data.conversation_id).catch(() => {});
      }
      loadConversations();
    } else if (data.type === 'message_edit') {
      if (cur && cur.id === data.conversation_id) {
        setMessages((prev) => prev.map((m) => m.id === data.message.id ? data.message : m));
      }
    } else if (data.type === 'message_delete') {
      if (cur && cur.id === data.conversation_id) {
        setMessages((prev) => prev.map((m) => m.id === data.message_id ? { ...m, deleted: true, text: '(deleted)' } : m));
      }
    } else if (data.type === 'message_reaction') {
      if (cur && cur.id === data.conversation_id) {
        setMessages((prev) => prev.map((m) => m.id === data.message.id ? { ...m, reactions: data.message.reactions } : m));
      }
    } else if (data.type === 'typing') {
      const { conversation_id, user_id, name, is_typing } = data;
      setTypingUsers((prev) => {
        const next = { ...prev };
        const forConv = { ...(next[conversation_id] || {}) };
        if (is_typing) {
          forConv[user_id] = { name, ts: Date.now() };
        } else {
          delete forConv[user_id];
        }
        next[conversation_id] = forConv;
        return next;
      });
    } else if (data.type === 'conversation_created' || data.type === 'conversation_updated' || data.type === 'conversation_removed') {
      loadConversations();
      if (data.type === 'conversation_removed' && cur && cur.id === data.conversation_id) {
        setSelected(null);
        setMessages([]);
      }
    }
  }, []);

  const { send: wsSend } = useWebSocket(handleWsMessage);

  useEffect(() => {
    const t = setInterval(() => {
      setTypingUsers((prev) => {
        const next = {};
        const now = Date.now();
        for (const cid of Object.keys(prev)) {
          const forConv = {};
          for (const uid of Object.keys(prev[cid])) {
            if (now - prev[cid][uid].ts < 5000) forConv[uid] = prev[cid][uid];
          }
          if (Object.keys(forConv).length) next[cid] = forConv;
        }
        return next;
      });
    }, 3000);
    return () => clearInterval(t);
  }, []);

  const loadConversations = async () => {
    try {
      const res = await userAPI.listConversations();
      setConversations(res.data);
      
      // Load pinned conversations
      const pinsRes = await fetch(`${API_URL}/api/user/conversations/pins`, {
        credentials: 'include'
      });
      if (pinsRes.ok) {
        const pinsData = await pinsRes.json();
        setPinnedIds(pinsData.pinned || []);
      }
    } catch (e) { /* noop */ }
  };

  const load = async () => {
    await loadConversations();
    const params = new URLSearchParams(location.search);
    const withUid = params.get('with');
    if (withUid && !selected) {
      try {
        const c = await userAPI.createConversation({ type: 'dm', participants: [withUid] });
        openConv(c.data);
        await loadConversations();
      } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed to open chat'); }
    }
  };

  useEffect(() => { load(); }, [location.search]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const openConv = async (c) => {
    setSelected(c);
    try {
      const res = await userAPI.listMessages(c.id);
      setMessages(res.data);
      await loadConversations();
    } catch (e) { toast.error('Failed'); }
  };

  const send = async () => {
    if (!text.trim() || !selected) return;
    setBusy(true);
    try {
      if (editing) {
        const res = await userAPI.editMessage(editing.id, text.trim());
        setMessages(messages.map((m) => m.id === editing.id ? res.data : m));
        setEditing(null);
      } else {
        const res = await userAPI.sendMessage(selected.id, text.trim());
        setMessages((prev) => {
          if (prev.some((m) => m.id === res.data.id)) return prev;
          return [...prev, res.data];
        });
      }
      setText('');
      wsSend({ type: 'typing', conversation_id: selected.id, is_typing: false });
      loadConversations();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
    finally { setBusy(false); }
  };

  const startEdit = (m) => { setEditing(m); setText(m.text); };

  const del = async (m) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await userAPI.deleteMessage(m.id);
      setMessages(messages.map((x) => x.id === m.id ? { ...x, text: '(deleted)', deleted: true } : x));
    } catch (e) { toast.error('Failed'); }
  };

  const react = async (m, emoji) => {
    setOpenReaction(null);
    try {
      const res = await userAPI.reactMessage(m.id, emoji);
      setMessages((prev) => prev.map((x) => x.id === m.id ? { ...x, reactions: res.data.reactions } : x));
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const blockOther = async () => {
    const other = selected.participants.find((p) => p.user_id !== user.id);
    if (!other) return;
    if (!window.confirm(`Block ${other.name}? They won't be able to message you.`)) return;
    try {
      await userAPI.blockUser(other.user_id);
      toast.success('Blocked');
      setSelected(null);
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const togglePin = async (cid) => {
    try {
      const isPinned = pinnedIds.includes(cid);
      if (isPinned) {
        await fetch(`${API_URL}/api/user/conversations/${cid}/pin`, {
          method: 'DELETE',
          credentials: 'include'
        });
        setPinnedIds(pinnedIds.filter(id => id !== cid));
        toast.success('Unpinned');
      } else {
        await fetch(`${API_URL}/api/user/conversations/${cid}/pin`, {
          method: 'POST',
          credentials: 'include'
        });
        setPinnedIds([...pinnedIds, cid]);
        toast.success('Pinned');
      }
    } catch (e) {
      toast.error('Failed to update pin');
    }
  };

  const onTextChange = (e) => {
    const v = e.target.value;
    setText(v);
    if (!selected) return;
    wsSend({ type: 'typing', conversation_id: selected.id, is_typing: true });
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      wsSend({ type: 'typing', conversation_id: selected.id, is_typing: false });
    }, 3000);
  };

  const getUserBadge = (participant) => {
    const role = participant.role || 'customer';
    if (participant.verified) return <ShieldCheck className="h-3 w-3 text-blue-500 inline ml-1" title="Verified" />;
    if (role === 'agent') return <Zap className="h-3 w-3 text-purple-500 inline ml-1" title="Agent" />;
    if (role === 'merchant') return <Store className="h-3 w-3 text-green-500 inline ml-1" title="Merchant" />;
    return null;
  };

  const searchUsers = async (query) => {
    setUserSearchQuery(query);
    if (query.length < 2) {
      setUserSearchResults([]);
      return;
    }

    try {
      setSearchingUsers(true);
      const res = await fetch(`${API_URL}/api/social/user/search?q=${encodeURIComponent(query)}`, {
        credentials: 'include'
      });
      
      if (res.ok) {
        const data = await res.json();
        setUserSearchResults(data.users || []);
      }
    } catch (error) {
      console.error('User search failed:', error);
    } finally {
      setSearchingUsers(false);
    }
  };

  const startChatWithUser = async (userId) => {
    try {
      const c = await userAPI.createConversation({ type: 'dm', participants: [userId] });
      setShowUserSearch(false);
      setUserSearchQuery('');
      setUserSearchResults([]);
      openConv(c.data);
      await loadConversations();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || 'Failed to start chat');
    }
  };

  const filteredConversations = searchQuery.trim()
    ? conversations.filter(c => 
        c.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.participants.some(p => p.name?.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : conversations;

  const sortedConversations = [...filteredConversations].sort((a, b) => {
    const aPin = pinnedIds.includes(a.id);
    const bPin = pinnedIds.includes(b.id);
    if (aPin && !bPin) return -1;
    if (!aPin && bPin) return 1;
    return new Date(b.last_message_at) - new Date(a.last_message_at);
  });

  const currentTyping = selected ? Object.values(typingUsers[selected.id] || {}).filter((t) => t.name) : [];
  const typingLabel = currentTyping.length === 0 ? '' :
    currentTyping.length === 1 ? `${currentTyping[0].name} is typing…` :
    `${currentTyping.map((t) => t.name).join(', ')} are typing…`;

  const renderMessage = (m) => {
    const mine = m.sender_id === user.id;
    const reactions = m.reactions || {};
    const reactionEntries = Object.entries(reactions);

    // Payment Request Card
    if (m.message_type === 'payment_request' && m.payment_request) {
      return <PaymentRequestCard key={m.id} message={m} mine={mine} onUpdate={loadConversations} />;
    }

    // Transaction Confirmation Card
    if (m.message_type === 'payment_confirmation') {
      return <TransactionConfirmationCard key={m.id} message={m} />;
    }

    // Voucher Share Card
    if (m.message_type === 'voucher_share' && m.voucher) {
      return <VoucherCard key={m.id} message={m} mine={mine} />;
    }

    // Regular message
    return (
      <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
        <div className={`max-w-[75%] rounded-xl p-3 group relative ${mine ? 'bg-gradient-to-br from-blue-500 to-blue-600 text-white' : 'bg-gray-100 dark:bg-gray-800'}`}>
          {!mine && selected.type === 'group' && (
            <p className="text-xs font-medium mb-1 opacity-80">{m.sender_name}</p>
          )}
          <p className={`text-sm whitespace-pre-wrap break-words ${m.deleted ? 'italic opacity-60' : ''}`}>{m.text}</p>
          <div className="text-[10px] opacity-70 mt-1 flex items-center gap-1 flex-wrap">
            {formatDate(m.created_at)}
            {m.edited_at && <span>· edited</span>}
            {!m.deleted && (
              <div className={`ml-2 flex gap-1 ${openReaction === m.id ? '' : 'opacity-0 group-hover:opacity-100 transition-opacity'}`}>
                <button onClick={() => setOpenReaction(openReaction === m.id ? null : m.id)}><Smile className="h-3 w-3 inline" /></button>
                {mine && (
                  <>
                    <button onClick={() => startEdit(m)}><Edit2 className="h-3 w-3 inline" /></button>
                    <button onClick={() => del(m)}><Trash2 className="h-3 w-3 inline" /></button>
                  </>
                )}
              </div>
            )}
          </div>
          {openReaction === m.id && (
            <div className="absolute z-10 -top-10 left-0 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-full shadow-lg px-2 py-1 flex gap-1">
              {REACTION_OPTIONS.map((emo) => (
                <button key={emo} onClick={() => react(m, emo)} className="hover:scale-125 transition-transform text-base">{emo}</button>
              ))}
            </div>
          )}
          {reactionEntries.length > 0 && (
            <div className="flex gap-1 mt-1 flex-wrap">
              {reactionEntries.map(([emo, uids]) => {
                const active = uids.includes(user.id);
                return (
                  <button
                    key={emo}
                    onClick={() => react(m, emo)}
                    className={`text-xs px-1.5 py-0.5 rounded-full border transition-colors ${active ? 'bg-white/20 border-white/40' : 'bg-white/10 border-white/20 hover:bg-white/20'}`}
                  >
                    {emo} {uids.length}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  };

  const LayoutWrapper = embeddedInConnect ? 'div' : FocusedLayout;
  const layoutProps = embeddedInConnect ? {} : { title: "Matrise Connect", backPath: "/connect/feed" };

  return (
    <LayoutWrapper {...layoutProps}>
      <div className="grid gap-4 md:grid-cols-[360px_1fr] h-[calc(100vh-8rem)]">
        {/* Conversations Sidebar */}
        <Card className={selected ? 'hidden md:block' : ''}>
          <CardContent className="pt-4 h-full overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <MessageSquare className="h-5 w-5 text-blue-600" />
                <h2 className="font-bold text-lg bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Chats</h2>
              </div>
              <div className="flex gap-1">
                <Button size="sm" variant="ghost" onClick={() => setShowUserSearch(true)} title="Start new chat">
                  <UserPlus className="h-3 w-3" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setShowNewGroup(true)}>
                  <Plus className="h-3 w-3" />
                </Button>
              </div>
            </div>

            {/* Search */}
            <div className="relative mb-3">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search chats..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {sortedConversations.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-8">
                {searchQuery ? 'No chats found' : 'No conversations yet'}
              </p>
            ) : (
              <div className="space-y-1">
                {sortedConversations.map((c) => {
                  const someTyping = Object.values(typingUsers[c.id] || {}).length > 0;
                  const isPinned = pinnedIds.includes(c.id);
                  return (
                    <div
                      key={c.id}
                      className={`relative group rounded-xl p-3 transition-all cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/20 ${selected?.id === c.id ? 'bg-blue-50 dark:bg-blue-900/30 border-l-4 border-blue-600' : ''}`}
                      onClick={() => openConv(c)}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {isPinned && <Pin className="h-3 w-3 text-blue-600 flex-shrink-0" />}
                          {c.type === 'group' && <Users className="h-3 w-3 flex-shrink-0 text-purple-600" />}
                          <p className="font-semibold truncate flex items-center gap-1">
                            {c.name || '(unnamed)'}
                            {c.participants[0] && getUserBadge(c.participants[0])}
                          </p>
                        </div>
                        <div className="flex items-center gap-1">
                          {c.unread_count > 0 && (
                            <Badge className="bg-blue-600 text-white">{c.unread_count}</Badge>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              togglePin(c.id);
                            }}
                            className="opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <Pin className={`h-3 w-3 ${isPinned ? 'text-blue-600 fill-blue-600' : 'text-gray-400'}`} />
                          </button>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground truncate mt-1">
                        {someTyping ? <span className="italic text-blue-600">typing…</span> : (c.last_message_preview || 'No messages yet')}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Chat Window */}
        <Card className={!selected ? 'hidden md:flex md:items-center md:justify-center' : 'flex flex-col'}>
          {!selected ? (
            <div className="text-center">
              <MessageSquare className="h-16 w-16 text-muted-foreground mx-auto mb-3 opacity-50" />
              <p className="text-muted-foreground">Select a conversation to start chatting</p>
            </div>
          ) : (
            <>
              {/* Header */}
              <div className="p-4 border-b flex items-center justify-between gap-3 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20">
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="icon" className="md:hidden" onClick={() => setSelected(null)}>
                    <ArrowLeft className="h-4 w-4" />
                  </Button>
                  <div>
                    <p className="font-bold flex items-center gap-1">
                      {selected.name}
                      {selected.participants[0] && getUserBadge(selected.participants[0])}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {selected.type === 'dm' ? 'Direct message' : `${selected.participants.length} members`}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {selected.type === 'dm' && (
                    <Button variant="outline" size="sm" onClick={() => setShowWalletActions(!showWalletActions)}>
                      <DollarSign className="h-3 w-3 mr-1" /> Wallet
                    </Button>
                  )}
                  {selected.type === 'group' && (
                    <Button variant="outline" size="sm" onClick={() => setShowManage(true)}>
                      <Users className="h-3 w-3 mr-1" /> Manage
                    </Button>
                  )}
                  {selected.type === 'dm' && (
                    <>
                      <Button variant="outline" size="sm" onClick={() => setShowReportUser(true)}>
                        <AlertCircle className="h-3 w-3" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={blockOther}>
                        <Ban className="h-3 w-3" />
                      </Button>
                    </>
                  )}
                </div>
              </div>

              {/* Wallet Actions Bar */}
              {showWalletActions && selected.type === 'dm' && (
                <div className="p-3 border-b bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20">
                  <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2">💰 WALLET ACTIONS</p>
                  <div className="flex gap-2 flex-wrap">
                    <Button size="sm" className="bg-blue-500 hover:bg-blue-600 text-white" onClick={() => setShowSendMoney(true)}>
                      <Send className="h-3 w-3 mr-1" /> Send Money
                    </Button>
                    <Button size="sm" className="bg-green-500 hover:bg-green-600 text-white" onClick={() => setShowPaymentRequest(true)}>
                      <CreditCard className="h-3 w-3 mr-1" /> Request Money
                    </Button>
                    <Button size="sm" className="bg-purple-500 hover:bg-purple-600 text-white">
                      <QrCode className="h-3 w-3 mr-1" /> Share QR
                    </Button>
                    <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white">
                      <History className="h-3 w-3 mr-1" /> History
                    </Button>
                    <Button size="sm" className="bg-pink-500 hover:bg-pink-600 text-white" onClick={() => setShowVoucherShare(true)}>
                      <Gift className="h-3 w-3 mr-1" /> Share Voucher
                    </Button>
                  </div>
                </div>
              )}

              {/* Messages */}
              <CardContent className="flex-1 overflow-y-auto p-4">
                <div className="space-y-3">
                  {messages.map(renderMessage)}
                  <div ref={endRef} />
                </div>
              </CardContent>

              {/* Typing Indicator */}
              {typingLabel && (
                <div className="px-4 pb-1 text-xs italic text-blue-600">{typingLabel}</div>
              )}

              {/* Input */}
              <div className="p-3 border-t bg-gray-50 dark:bg-gray-900">
                {editing && (
                  <div className="flex items-center justify-between text-xs mb-2 px-2 py-1 rounded bg-amber-100 dark:bg-amber-900/20 text-amber-800 dark:text-amber-400">
                    <span>Editing message</span>
                    <button onClick={() => { setEditing(null); setText(''); }}>Cancel</button>
                  </div>
                )}
                <div className="flex gap-2">
                  <Textarea
                    rows={1}
                    value={text}
                    onChange={onTextChange}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Type a message..."
                    className="resize-none"
                  />
                  <Button onClick={send} disabled={busy || !text.trim()} className="bg-gradient-to-r from-blue-600 to-purple-600">
                    <Send className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>

      {/* Dialogs */}
      <NewGroupDialog open={showNewGroup} onOpenChange={setShowNewGroup} onCreated={(c) => { loadConversations(); openConv(c); }} />
      <ManageGroupDialog
        open={showManage}
        onOpenChange={setShowManage}
        conversation={selected}
        currentUserId={user?.id}
        onChanged={async () => {
          await loadConversations();
          if (selected) {
            const fresh = (await userAPI.listConversations()).data.find((c) => c.id === selected.id);
            if (fresh) setSelected(fresh);
          }
        }}
        onLeft={() => { setSelected(null); setMessages([]); loadConversations(); }}
      />
      <PaymentRequestDialog
        open={showPaymentRequest}
        onOpenChange={setShowPaymentRequest}
        conversationId={selected?.id}
        onCreated={() => { setShowPaymentRequest(false); loadConversations(); }}
      />
      <SendMoneyDialog
        open={showSendMoney}
        onOpenChange={setShowSendMoney}
        conversation={selected}
        onSent={() => { setShowSendMoney(false); loadConversations(); }}
      />
      <VoucherShareDialog
        open={showVoucherShare}
        onOpenChange={setShowVoucherShare}
        conversationId={selected?.id}
        onShared={() => { setShowVoucherShare(false); loadConversations(); }}
      />
      <ReportUserDialog
        open={showReportUser}
        onOpenChange={setShowReportUser}
        user={selected?.participants?.find(p => p.user_id !== user.id)}
        onReported={() => setShowReportUser(false)}
      />
      
      {/* User Search Dialog for Starting New Chats */}
      <Dialog open={showUserSearch} onOpenChange={setShowUserSearch}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Start New Chat</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={userSearchQuery}
                onChange={(e) => searchUsers(e.target.value)}
                className="pl-9"
                autoFocus
              />
            </div>
            
            <div className="max-h-80 overflow-y-auto space-y-2">
              {searchingUsers ? (
                <p className="text-sm text-muted-foreground text-center py-4">Searching...</p>
              ) : userSearchResults.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  {userSearchQuery ? 'No users found' : 'Type to search users'}
                </p>
              ) : (
                userSearchResults.map(user => (
                  <div
                    key={user.id}
                    className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-muted transition-colors"
                    onClick={() => startChatWithUser(user.id)}
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white font-bold">
                      {user.profile_picture ? (
                        <img src={user.profile_picture} alt={user.display_name} className="w-full h-full rounded-full object-cover" />
                      ) : (
                        user.display_name?.[0]?.toUpperCase()
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{user.display_name}</span>
                        {user.verified && <CheckCircle className="h-3 w-3 text-blue-500" />}
                      </div>
                      <span className="text-sm text-muted-foreground">@{user.username}</span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </LayoutWrapper>
  );
};

// ============ Supporting Components ============

const PaymentRequestCard = ({ message, mine, onUpdate }) => {
  const pr = message.payment_request;
  const [busy, setBusy] = useState(false);

  const handlePay = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${API_URL}/api/user/payment-requests/${pr.payment_request_id}/pay`, {
        method: 'POST',
        credentials: 'include'
      });
      if (res.ok) {
        toast.success('Payment completed!');
        onUpdate();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Payment failed');
      }
    } catch (e) {
      toast.error('Payment failed');
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async () => {
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/user/payment-requests/${pr.payment_request_id}/decline`, {
        method: 'POST',
        credentials: 'include'
      });
      toast.success('Request declined');
      onUpdate();
    } catch (e) {
      toast.error('Failed');
    } finally {
      setBusy(false);
    }
  };

  const statusColor = {
    pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400',
    paid: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400',
    declined: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400',
    expired: 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400'
  }[pr.status] || '';

  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className="max-w-[75%] border-2 border-blue-200 dark:border-blue-800 rounded-xl p-4 bg-white dark:bg-gray-800">
        <div className="flex items-center gap-2 mb-2">
          <CreditCard className="h-5 w-5 text-blue-600" />
          <p className="font-semibold">Payment Request</p>
          <Badge className={statusColor}>{pr.status}</Badge>
        </div>
        <div className="space-y-2">
          <div className="flex items-baseline gap-2">
            <p className="text-2xl font-bold text-blue-600">{pr.amount}</p>
            <p className="text-sm text-muted-foreground">{pr.currency}</p>
          </div>
          {pr.note && <p className="text-sm text-muted-foreground">{pr.note}</p>}
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatDate(message.created_at)}
          </p>
          {pr.status === 'pending' && !mine && (
            <div className="flex gap-2 mt-3">
              <Button size="sm" onClick={handlePay} disabled={busy} className="flex-1">
                <Check className="h-3 w-3 mr-1" /> Pay Now
              </Button>
              <Button size="sm" variant="outline" onClick={handleDecline} disabled={busy}>
                <X className="h-3 w-3 mr-1" /> Decline
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const TransactionConfirmationCard = ({ message }) => {
  return (
    <div className="flex justify-center">
      <div className="border border-green-200 dark:border-green-800 rounded-xl p-3 bg-green-50 dark:bg-green-900/20 max-w-sm">
        <div className="flex items-center gap-2 mb-1">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <p className="text-sm font-semibold text-green-800 dark:text-green-400">{message.text}</p>
        </div>
        <p className="text-xs text-muted-foreground">
          {formatDate(message.created_at)} · TX: {message.transaction_id?.slice(0, 8)}...
        </p>
      </div>
    </div>
  );
};

const VoucherCard = ({ message, mine }) => {
  const voucher = message.voucher;
  return (
    <div className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
      <div className="max-w-[75%] border-2 border-purple-200 dark:border-purple-800 rounded-xl p-4 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20">
        <div className="flex items-center gap-2 mb-2">
          <Gift className="h-5 w-5 text-purple-600" />
          <p className="font-semibold">Voucher</p>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 font-mono text-lg font-bold text-center border-2 border-dashed border-purple-400 dark:border-purple-600">
          {voucher.code}
        </div>
        {voucher.description && <p className="text-sm text-muted-foreground mt-2">{voucher.description}</p>}
        {voucher.value && <p className="text-sm font-semibold mt-1">Value: {voucher.value}</p>}
        {!voucher.valid && <p className="text-xs text-red-600 mt-1">⚠️ Voucher not found in system</p>}
      </div>
    </div>
  );
};

// ============ NEW GROUP DIALOG ============
const NewGroupDialog = ({ open, onOpenChange, onCreated }) => {
  const [name, setName] = useState('');
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setName(''); setSelected([]); setSearch(''); return; }
    (async () => {
      try {
        const res = await userAPI.getDirectory(search);
        setUsers(res.data || []);
      } catch (e) { 
        console.error('Failed to load directory:', e);
        setUsers([]);
      }
    })();
  }, [open, search]);

  const toggle = (uid) => {
    setSelected((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
  };

  const create = async () => {
    if (!name.trim()) return toast.error('Group name required');
    if (selected.length < 2) return toast.error('Pick at least 2 people');
    setBusy(true);
    try {
      const res = await userAPI.createConversation({ type: 'group', name: name.trim(), participants: selected });
      toast.success('Group created');
      onOpenChange(false);
      onCreated?.(res.data);
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Create a Group</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Group name" value={name} onChange={(e) => setName(e.target.value)} maxLength={80} />
          <Input placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)} />
          <div className="max-h-64 overflow-y-auto space-y-1 border rounded-lg p-2">
            {users.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4">No users found</p> :
              users.map((u) => (
                <label key={u.id} className="flex items-center gap-2 p-2 rounded hover:bg-muted cursor-pointer">
                  <Checkbox checked={selected.includes(u.id)} onCheckedChange={() => toggle(u.id)} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">@{u.username || u.email?.split('@')[0] || 'user'}</p>
                    <p className="text-xs text-muted-foreground truncate">{u.name || 'User'}</p>
                  </div>
                </label>
              ))
            }
          </div>
          <p className="text-xs text-muted-foreground">Selected: {selected.length}</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={create} disabled={busy}><Plus className="h-4 w-4 mr-1" /> Create</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ MANAGE GROUP DIALOG ============
const ManageGroupDialog = ({ open, onOpenChange, conversation, currentUserId, onChanged, onLeft }) => {
  const [directory, setDirectory] = useState([]);
  const [search, setSearch] = useState('');
  const isCreator = conversation?.created_by === currentUserId;

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await userAPI.getDirectory(search);
        setDirectory(res.data);
      } catch (e) { /* noop */ }
    })();
  }, [open, search]);

  if (!conversation) return null;

  const memberIds = new Set(conversation.participants.map((p) => p.user_id));
  const addable = directory.filter((u) => !memberIds.has(u.id));

  const addMember = async (uid) => {
    try {
      await userAPI.addGroupMember(conversation.id, uid);
      toast.success('Member added');
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const removeMember = async (uid) => {
    if (!window.confirm('Remove this member?')) return;
    try {
      await userAPI.removeGroupMember(conversation.id, uid);
      toast.success('Member removed');
      onChanged?.();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  const leave = async () => {
    if (!window.confirm('Leave this group?')) return;
    try {
      await userAPI.leaveGroup(conversation.id);
      toast.success('Left group');
      onOpenChange(false);
      onLeft?.();
    } catch (e) { toast.error(formatApiErrorDetail(e) || 'Failed'); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Manage &quot;{conversation.name}&quot;</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium mb-2">Members ({conversation.participants.length})</p>
            <div className="max-h-40 overflow-y-auto space-y-1 border rounded-lg p-2">
              {conversation.participants.map((p) => (
                <div key={p.user_id} className="flex items-center justify-between gap-2 p-1.5 rounded hover:bg-muted">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">
                      {p.name} {p.user_id === conversation.created_by && <Badge variant="secondary" className="ml-1 text-[10px]">Creator</Badge>}
                    </p>
                  </div>
                  {isCreator && p.user_id !== currentUserId && (
                    <Button variant="ghost" size="icon" onClick={() => removeMember(p.user_id)}>
                      <UserMinus className="h-4 w-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {isCreator && (
            <div>
              <p className="text-sm font-medium mb-2">Add members</p>
              <Input placeholder="Search users..." value={search} onChange={(e) => setSearch(e.target.value)} className="mb-2" />
              <div className="max-h-32 overflow-y-auto space-y-1 border rounded-lg p-2">
                {addable.length === 0 ? <p className="text-xs text-muted-foreground text-center py-2">No one else to add</p> :
                  addable.slice(0, 20).map((u) => (
                    <div key={u.id} className="flex items-center justify-between gap-2 p-1.5 rounded hover:bg-muted">
                      <p className="text-sm truncate">@{u.username || u.email?.split('@')[0] || 'user'}</p>
                      <Button variant="ghost" size="icon" onClick={() => addMember(u.id)}>
                        <UserPlus className="h-4 w-4 text-blue-500" />
                      </Button>
                    </div>
                  ))
                }
              </div>
            </div>
          )}

          <Button variant="outline" onClick={leave} className="w-full text-red-500 hover:text-red-600">
            <LogOut className="h-4 w-4 mr-2" /> Leave group
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============ PAYMENT REQUEST DIALOG ============
const PaymentRequestDialog = ({ open, onOpenChange, conversationId, onCreated }) => {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('ZAR');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const createRequest = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      return toast.error('Enter valid amount');
    }
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/user/conversations/${conversationId}/payment-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          amount: parseFloat(amount),
          currency,
          note: note.trim()
        })
      });
      toast.success('Payment request sent!');
      setAmount('');
      setNote('');
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error('Failed to send request');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Request Payment</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Amount</Label>
            <Input
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="0"
              step="0.01"
            />
          </div>
          <div>
            <Label>Currency</Label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full border rounded-md p-2"
            >
              <option value="ZAR">ZAR</option>
              <option value="USD">USD</option>
              <option value="KCRD">KCRD</option>
            </select>
          </div>
          <div>
            <Label>Note (optional)</Label>
            <Textarea
              placeholder="What is this for?"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={createRequest} disabled={busy}>
            <CreditCard className="h-4 w-4 mr-1" /> Request
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ SEND MONEY DIALOG ============
const SendMoneyDialog = ({ open, onOpenChange, conversation, onSent }) => {
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('ZAR');
  const [busy, setBusy] = useState(false);

  const sendMoney = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      return toast.error('Enter valid amount');
    }
    if (!conversation || conversation.type !== 'dm') {
      return toast.error('Can only send money in direct messages');
    }

    const recipient = conversation.participants.find(p => p.user_id !== conversation.participants[0].user_id);
    if (!recipient) return;

    setBusy(true);
    try {
      // Use existing transfer endpoint
      const res = await fetch(`${API_URL}/api/user/transfer`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          recipient_email: recipient.email,
          amount: parseFloat(amount),
          currency
        })
      });

      if (res.ok) {
        toast.success('Money sent!');
        setAmount('');
        onOpenChange(false);
        onSent?.();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Transfer failed');
      }
    } catch (e) {
      toast.error('Failed to send money');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Send Money</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Amount</Label>
            <Input
              type="number"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              min="0"
              step="0.01"
            />
          </div>
          <div>
            <Label>Currency</Label>
            <select
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
              className="w-full border rounded-md p-2"
            >
              <option value="ZAR">ZAR</option>
              <option value="USD">USD</option>
              <option value="KCRD">KCRD</option>
            </select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={sendMoney} disabled={busy}>
            <Send className="h-4 w-4 mr-1" /> Send
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ VOUCHER SHARE DIALOG ============
const VoucherShareDialog = ({ open, onOpenChange, conversationId, onShared }) => {
  const [vouchers, setVouchers] = useState([]);
  const [selected, setSelected] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const res = await userAPI.getUserVouchers();
        setVouchers(res.data || []);
      } catch (e) { /* noop */ }
    })();
  }, [open]);

  const share = async () => {
    if (!selected) return toast.error('Select a voucher');
    setBusy(true);
    try {
      await fetch(`${API_URL}/api/user/conversations/${conversationId}/share-voucher`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ voucher_code: selected })
      });
      toast.success('Voucher shared!');
      setSelected('');
      onOpenChange(false);
      onShared?.();
    } catch (e) {
      toast.error('Failed to share voucher');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Share a Voucher</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {vouchers.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">No vouchers available</p>
          ) : (
            <div className="space-y-2">
              {vouchers.filter(v => v.status === 'active').map((v) => (
                <label key={v.code} className="flex items-center gap-2 p-3 border rounded-lg cursor-pointer hover:bg-muted">
                  <input
                    type="radio"
                    name="voucher"
                    value={v.code}
                    checked={selected === v.code}
                    onChange={(e) => setSelected(e.target.value)}
                    className="form-radio"
                  />
                  <div className="flex-1">
                    <p className="font-mono font-semibold">{v.code}</p>
                    <p className="text-xs text-muted-foreground">
                      Value: {v.value} {v.currency}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={share} disabled={busy || !selected}>
            <Gift className="h-4 w-4 mr-1" /> Share
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============ REPORT USER DIALOG ============
const ReportUserDialog = ({ open, onOpenChange, user: reportedUser, onReported }) => {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason) return toast.error('Select a reason');
    if (!reportedUser || !reportedUser.user_id) {
      toast.error('Unable to report user');
      return;
    }

    setBusy(true);
    try {
      await fetch(`${API_URL}/api/user/report/${reportedUser.user_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ reason, details: details.trim() })
      });
      toast.success('Report submitted. Thank you.');
      setReason('');
      setDetails('');
      onOpenChange(false);
      onReported?.();
    } catch (e) {
      toast.error('Failed to submit report');
    } finally {
      setBusy(false);
    }
  };

  if (!reportedUser) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Report User</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Reason</Label>
            <select
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full border rounded-md p-2"
            >
              <option value="">Select reason...</option>
              <option value="spam">Spam</option>
              <option value="harassment">Harassment</option>
              <option value="inappropriate">Inappropriate content</option>
              <option value="fraud">Fraud or scam</option>
              <option value="impersonation">Impersonation</option>
              <option value="other">Other</option>
            </select>
          </div>
          <div>
            <Label>Additional details (optional)</Label>
            <Textarea
              placeholder="Provide more context..."
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={busy} variant="destructive">
            <AlertCircle className="h-4 w-4 mr-1" /> Submit Report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );



};

